import type {
  PublicHelpFlatPage,
  PublicHelpSpace,
} from "@v1/backend/logic/pages/public_help_space_groups";
import type { HelpSpaTarget } from "./help-spa-path";

/** One page of the public tree with its loaded children, in position order. */
export type HelpTreeNode = {
  readonly page: PublicHelpFlatPage;
  readonly children: readonly HelpTreeNode[];
};

/** Rebuilds a space's tree from DFS-ordered pages. Parent outside the nav budget → root. */
export function buildHelpSpaceTree(
  pages: readonly PublicHelpFlatPage[],
): HelpTreeNode[] {
  const childrenById = new Map<string, HelpTreeNode[]>();
  const nodes: HelpTreeNode[] = pages.map((page) => {
    const children: HelpTreeNode[] = [];
    childrenById.set(page.page_id, children);
    return { page, children };
  });
  const roots: HelpTreeNode[] = [];
  for (const node of nodes) {
    const parentId = node.page.parent_page_id;
    const siblings =
      parentId && parentId !== node.page.page_id
        ? childrenById.get(parentId)
        : undefined;
    if (siblings) siblings.push(node);
    else roots.push(node);
  }
  return roots;
}

/** Where a page sits in the loaded navigation. */
export type HelpPageLocation = {
  readonly spaceId: string;
  /** Loaded ancestors, root first; the page itself excluded. */
  readonly ancestorIds: readonly string[];
};

/** Null when the page is not part of the loaded navigation (truncated nav). */
export function locateHelpPage(
  spaces: readonly PublicHelpSpace[],
  pageId: string,
): HelpPageLocation | null {
  for (const space of spaces) {
    const byId = new Map(space.pages.map((page) => [page.page_id, page]));
    const page = byId.get(pageId);
    if (!page) continue;
    const ancestorIds: string[] = [];
    const seen = new Set<string>([pageId]);
    let parentId = page.parent_page_id;
    while (parentId && !seen.has(parentId) && byId.has(parentId)) {
      seen.add(parentId);
      ancestorIds.unshift(parentId);
      parentId = byId.get(parentId)?.parent_page_id ?? null;
    }
    return { spaceId: space.space_id, ancestorIds };
  }
  return null;
}

export function helpNavSpaceKey(spaceId: string): string {
  return `s:${spaceId}`;
}

export function helpNavPageKey(pageId: string): string {
  return `p:${pageId}`;
}

/** Sidebar state derived from the current target. */
export type HelpNavPath = {
  /** Row of the current target; null on the start page or when not loaded. */
  readonly currentKey: string | null;
  /** Current row plus every loaded ancestor, space included. */
  readonly pathKeys: ReadonlySet<string>;
};

const EMPTY_NAV_PATH: HelpNavPath = { currentKey: null, pathKeys: new Set() };

export function resolveHelpNavPath(
  spaces: readonly PublicHelpSpace[],
  target: HelpSpaTarget | null,
): HelpNavPath {
  if (!target || target.kind === "center") return EMPTY_NAV_PATH;
  if (target.kind === "space") {
    const key = helpNavSpaceKey(target.spaceId);
    return { currentKey: key, pathKeys: new Set([key]) };
  }
  const location = locateHelpPage(spaces, target.pageId);
  if (!location) return EMPTY_NAV_PATH;
  const currentKey = helpNavPageKey(target.pageId);
  return {
    currentKey,
    pathKeys: new Set([
      helpNavSpaceKey(location.spaceId),
      ...location.ancestorIds.map(helpNavPageKey),
      currentKey,
    ]),
  };
}
